/**
 * Admin › Finanças › Acertos (W9-DRIVER-SETTLEMENT-MODES, D469, ADR-0087; DevMobility F-011–F-014, F-050) — the pure
 * view-model behind the screen, over `@vello/api-client` `SettlementAdminApi`.
 *
 * - the tone of a settlement standing and the copy id of each mode, status, plan kind and payment method;
 * - whether THIS operator may decide a settlement change (never the one who proposed it, never about his own account —
 *   the server refuses `SELF_APPROVAL` / `SELF_ADJUSTMENT` anyway; the screen hides the button and says why);
 * - the draft checks of a plan and of a settlement payment before anything is posted, and the words of each refusal;
 * - exact per-currency totals of a report (bigint, never summed across currencies).
 * Nothing here names a country, a currency or an amount.
 */
import type { SettlementChangeView, SettlementReportRowView, SettlementStandingView } from '@vello/api-client';

export type SettlementTone = 'neutral' | 'success' | 'warning' | 'danger';

export function settlementTone(s: Pick<SettlementStandingView, 'status'>): SettlementTone {
  return s.status === 'OK' ? 'success' : s.status === 'BLOCKED' ? 'danger' : s.status === 'DUE' || s.status === 'EXPIRING' ? 'warning' : 'neutral';
}

/** The copy id of a mode (`settle.mode.<MODE>`), of a standing reason (`settle.reason.<CODE>`) — unknown codes read raw. */
export const settlementModeKey = (mode: string): string => `settle.mode.${mode}`;
export const settlementReasonKey = (code: string): string => `settle.reason.${code}`;

/** Why this operator may not approve a settlement change (or undefined when he may). */
/** D868: `selfApproval` lifts OWN_PROPOSAL for a super administrator; OWN_ACCOUNT (a change to one's own driver account) stays. */
export function settlementApproveBlock(change: Pick<SettlementChangeView, 'proposedBy' | 'status' | 'driverId'>, operatorId: string | undefined, mayApprove: boolean, selfApproval = false): 'NOT_PENDING' | 'NO_PERMISSION' | 'OWN_PROPOSAL' | 'OWN_ACCOUNT' | undefined {
  if (change.status !== 'PENDING') return 'NOT_PENDING';
  if (!mayApprove) return 'NO_PERMISSION';
  if (!selfApproval && operatorId !== undefined && change.proposedBy === operatorId) return 'OWN_PROPOSAL';
  if (operatorId !== undefined && change.driverId === operatorId) return 'OWN_ACCOUNT';
  return undefined;
}

/** The draft issue of a period plan, or undefined (the server re-checks every one). */
export function planDraftIssue(d: { readonly name: string; readonly kind: string; readonly count: number; readonly priceMinor: bigint | undefined }): 'PLAN_NAME_INVALID' | 'PLAN_KIND_INVALID' | 'PLAN_COUNT_INVALID' | 'PLAN_PRICE_INVALID' | undefined {
  if (d.name.trim() === '' || d.name.length > 80) return 'PLAN_NAME_INVALID';
  if (d.kind !== 'DAYS' && d.kind !== 'WEEKDAY' && d.kind !== 'MONTH_DAY') return 'PLAN_KIND_INVALID';
  const max = d.kind === 'DAYS' ? 366 : d.kind === 'WEEKDAY' ? 7 : 31;
  if (!Number.isInteger(d.count) || d.count < 1 || d.count > max) return 'PLAN_COUNT_INVALID';
  if (d.priceMinor === undefined || d.priceMinor <= 0n) return 'PLAN_PRICE_INVALID';
  return undefined;
}

/** The draft issue of a settlement payment: drivers chosen, and a PERIOD names a valid local-day range. */
export function paymentDraftIssue(d: { readonly driverIds: readonly string[]; readonly basis: 'TOTAL' | 'PERIOD'; readonly from: string; readonly to: string }): 'DRIVERS_REQUIRED' | 'TOO_MANY_DRIVERS' | 'SETTLEMENT_PERIOD_INVALID' | undefined {
  if (d.driverIds.length === 0) return 'DRIVERS_REQUIRED';
  if (d.driverIds.length > 50) return 'TOO_MANY_DRIVERS';
  const day = /^\d{4}-\d{2}-\d{2}$/;
  if (d.basis === 'PERIOD' && (!day.test(d.from) || !day.test(d.to) || d.from > d.to)) return 'SETTLEMENT_PERIOD_INVALID';
  return undefined;
}

/** Exact totals of a report: how much the drivers owe and how many are overdue / blocked. */
export function summarizeSettlement(rows: readonly SettlementReportRowView[]): { readonly owedMinor: bigint; readonly currency: string | undefined; readonly overdue: number; readonly blocked: number; readonly drivers: number } {
  let owed = 0n;
  let currency: string | undefined;
  let overdue = 0;
  let blocked = 0;
  for (const r of rows) {
    if (currency === undefined) currency = r.balance.currency;
    if (r.balance.currency === currency && /^-\d+$/.test(r.balance.amountMinor)) owed += -BigInt(r.balance.amountMinor);
    if (r.settlement.reasonCode === 'DRIVER_SETTLEMENT_OVERDUE') overdue += 1;
    if (r.settlement.status === 'BLOCKED') blocked += 1;
  }
  return { owedMinor: owed, currency, overdue, blocked, drivers: rows.length };
}

